import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Icons } from '../../theme/Images'
import LabeledField from './LabeledField'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'

export default function LabeledSelect({ 
  label, 
  options, 
  value, 
  onChange, 
  className = '', 
  selectClassName = '', 
  placeholder = 'Select...',
  onValidate,
  triggerValidation,
  disabled,
  ...props 
}) {
  const [isValid, setIsValid] = useState(true)
  const [hasBlurred, setHasBlurred] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const validateField = () => {
    setHasBlurred(true)
    
    // Default validation: check if field is empty
    const isEmpty = !value
    
    if (isEmpty) {
      setIsValid(false)
      setErrorMessage('This field is required')
    } else {
      setIsValid(true)
      setErrorMessage('')
    }
    
    // Call custom validation if provided
    if (onValidate) {
      onValidate(value, !isEmpty)
    }
  }

  const handleValueChange = (newValue) => {
    onChange(newValue)
    
    // Clear error when a value is selected
    if (newValue) {
      setIsValid(true)
      setErrorMessage('')
    }
  }

  const handleOpenChange = (open) => {
    if (!open) {
      validateField()
    }
  }

  // Listen for external validation trigger
  useEffect(() => {
    if (triggerValidation) {
      validateField()
    }
  }, [triggerValidation])

  const getSelectClasses = () => {
    const baseClasses = "text-[16px] leading-[24px] border px-3 pt-3 pb-2 font-[300]"
    
    if (disabled) {
      return `${baseClasses} border-[color:var(--Grays-Gray-5,#ECECEC)] cursor-not-allowed bg-gray-50 ${selectClassName}`
    }
    
    if (hasBlurred && !isValid) {
      return `${baseClasses} border-error-300 focus:ring-error-300 focus:border-error-300 bg-white ${selectClassName}`
    }
    
    return `${baseClasses} border-[color:var(--Grays-Gray-5,#ECECEC)] bg-white ${selectClassName}`
  }

  return (
    <LabeledField label={label} className={className} validationError={errorMessage}>
      <div className="relative">
        <Select 
          value={value} 
          onValueChange={handleValueChange} 
          onOpenChange={handleOpenChange}
          {...props}
        >
          <SelectTrigger 
            className={cn(
              getSelectClasses(),
              disabled && '[&>svg]:text-[color:var(--Grays-Gray,#5D5D5D)] [&_span]:text-[color:var(--Grays-Gray,#5D5D5D)]'
            )}
            showIcon={!(hasBlurred && !isValid)}
            disabled={disabled}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options && options.map((opt, i) =>
              <SelectItem key={opt.value ?? i} value={opt.value}>{opt.label}</SelectItem>
            )}
          </SelectContent>
        </Select>
        {hasBlurred && !isValid && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Icons.FailureAlertIconSimple />
          </div>
        )}
      </div>
    </LabeledField>
  )
}

LabeledSelect.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  placeholder: PropTypes.string,
  onValidate: PropTypes.func,
  triggerValidation: PropTypes.bool,
  disabled: PropTypes.bool,
} 